import haversine, { Options } from 'haversine';
import { isFinite, isNil, round, toNumber, cloneDeep } from 'lodash';
import { ConsolidatedLocationView, LocationView } from '@mark43/cad-api';
import type { GeoPoint } from './types';
import type { GeoPointLocation, TimestampedGeoPoint } from '../esri/types';
import { NormalizedPhoneCallDisplay } from '../phone-calls/types';
import { EventQueueRowData } from '../socket/stream-types';

export const isValidLatLng = (
    latitude?: number | string | null,
    longitude?: number | string | null
): boolean => {
    if (isNil(latitude) || isNil(longitude)) {
        return false;
    }
    const strLat = `${latitude}`;
    const strLng = `${longitude}`;
    return (
        strLat.length > 0 &&
        strLng.length > 0 &&
        Math.abs(toNumber(strLat)) <= 90 &&
        Math.abs(toNumber(strLng)) <= 180
    );
};

export const roundLatLng = (
    latOrLng: number | string | undefined | null,
    decimalPlace = 2
): number | string | null | undefined => {
    if (isNil(latOrLng)) {
        return latOrLng;
    }
    if (isFinite(latOrLng)) {
        // @ts-expect-error latOrLng is number or string
        return round(latOrLng, decimalPlace);
    }
    if (isFinite(toNumber(latOrLng))) {
        return round(toNumber(latOrLng), decimalPlace);
    }
    return latOrLng;
};

export const extractLatLngFromBeginningOfAddressString = (
    str?: string
): { latitude: number; longitude: number } | undefined => {
    if (!str) {
        return;
    }
    // must start with lat/lng. Address string has format of `40.7128, -74.0006 New York, NY 10001 UNITED STATES`.
    // if the string is not start with lat/lng, regex will not match.
    const regex = /^([-+]?[0-9]*\.?[0-9]+),\s*([-+]?[0-9]*\.?[0-9]+)/;
    const match = str.match(regex);

    if (!match) {
        return;
    }

    const latitude = parseFloat(match[1]);
    const longitude = parseFloat(match[2]);
    if (isValidLatLng(latitude, longitude)) {
        return { latitude, longitude };
    }
    return;
};

const DECIMAL_FACTOR = 1000000;
const TOLERANCE_THRESHOLD = 15;
function toComparableInteger(num: number | string) {
    return Math.floor(parseFloat(`${num}`) * DECIMAL_FACTOR);
}

type LatLngLocation =
    | GeoPoint
    | GeoPointLocation
    | TimestampedGeoPoint
    | LocationView
    | ConsolidatedLocationView
    | NormalizedPhoneCallDisplay;
export function areLatLngsEqual(location1?: LatLngLocation, location2?: LatLngLocation): boolean {
    if (!location1 || !location2) {
        return !location1 && !location2;
    }
    const lat1 = location1.latitude ?? '';
    const lng1 = location1.longitude ?? '';
    const lat2 = location2.latitude ?? '';
    const lng2 = location2.longitude ?? '';

    if (lat1 === '' || lng1 === '' || lat2 === '' || lng2 === '') {
        return lat1 === lat2 && lng1 === lng2;
    }

    return (
        Math.abs(toComparableInteger(lat1) - toComparableInteger(lat2)) <= TOLERANCE_THRESHOLD &&
        Math.abs(toComparableInteger(lng1) - toComparableInteger(lng2)) <= TOLERANCE_THRESHOLD
    );
}

// check if a given lat & lng is within bounding box.
// NOTE: this doesn't work for bounding box across lng = 180,
// to check bounding box across lng = 180, calculate bounding box from west bound to lng +180 and
// lng -180 to east bound separately
const isInBoundingBox = (
    latitude: number,
    longitude: number,
    westBoundLng: number,
    eastBoundLng: number,
    northBoundLat: number,
    southBoundLat: number
): boolean => {
    return (
        latitude >= southBoundLat &&
        latitude <= northBoundLat &&
        longitude >= westBoundLng &&
        longitude <= eastBoundLng
    );
};
export function geoPointIsWithinBoundingBox(
    geoPoint: GeoPoint | TimestampedGeoPoint,
    nwCorner: GeoPoint,
    seCorner: GeoPoint
) {
    const { latitude: geoPointLatitude, longitude: geoPointLongitude } = geoPoint;
    if (typeof geoPointLatitude !== 'number' || typeof geoPointLongitude !== 'number') {
        return false;
    }
    // outside if there is a loop
    const { latitude: seCornerLat, longitude: seCornerLng } = seCorner;
    const { latitude: nwCornerLat, longitude: nwCornerLng } = nwCorner;

    // 3 special cases,
    // 1. west bound is on the left side of lng = 180 and east bound is on the right side.
    // 2. nwCornerLng > seCornerLng && nwCornerLng > 0 && seCornerLng > 0,
    // 3. nwCornerLng > seCornerLng && nwCornerLng < 0 && seCornerLng < 0
    // all 3 cases across the lng = 180, split bounding box to 2 by lng = 180
    // check if given lng is between [west bound, 180] or [-180, east bound]
    if (
        (nwCornerLng > 0 && seCornerLng < 0) ||
        (nwCornerLng > seCornerLng &&
            ((nwCornerLng > 0 && seCornerLng > 0) || (nwCornerLng < 0 && seCornerLng < 0)))
    ) {
        return (
            isInBoundingBox(
                geoPointLatitude,
                geoPointLongitude,
                -180,
                seCornerLng,
                nwCornerLat,
                seCornerLat
            ) ||
            isInBoundingBox(
                geoPointLatitude,
                geoPointLongitude,
                nwCornerLng,
                180,
                nwCornerLat,
                seCornerLat
            )
        );
    }
    // for all other cases, calculate once. This will handle lng (-180, 180) and lat (-90, 90)
    return isInBoundingBox(
        geoPointLatitude,
        geoPointLongitude,
        nwCornerLng,
        seCornerLng,
        nwCornerLat,
        seCornerLat
    );
}

export function getFullLocationAddressForDisplay({
    placeName,
    displayLineFirst,
    displayLineOptionalMiddle,
    displayLineLast,
}: {
    placeName?: string;
    displayLineFirst?: string;
    displayLineOptionalMiddle?: string;
    displayLineLast?: string;
}): string {
    if (!placeName && !displayLineFirst && !displayLineOptionalMiddle && !displayLineLast) {
        return '';
    }

    if (placeName && displayLineOptionalMiddle) {
        return `${displayLineFirst} ${placeName}, ${displayLineOptionalMiddle}`;
    }

    if (!placeName && displayLineOptionalMiddle) {
        return `${displayLineFirst}, ${displayLineOptionalMiddle}`;
    }

    return `${displayLineFirst}, ${displayLineLast}`;
}

export function getHaversineDistanceBetweenTwoEntities({
    firstEntityCoordinates,
    secondEntityCoordinates,
    unitOfMeasurement = 'mile',
}: {
    firstEntityCoordinates: GeoPoint;
    secondEntityCoordinates: GeoPoint;
    unitOfMeasurement?: Options['unit'];
}): number {
    return Math.round(
        haversine(firstEntityCoordinates, secondEntityCoordinates, {
            unit: unitOfMeasurement,
        })
    );
}

export function getEventLocation(event: EventQueueRowData): GeoPoint {
    return { latitude: event.latitude!, longitude: event.longitude! };
}

export function getDistanceFromEvent(
    event: EventQueueRowData,
    unitLocation: GeoPoint
): number | undefined {
    const isValidLocation = isValidLatLng(event.latitude, event.longitude);
    if (isValidLocation) {
        return Math.round(
            haversine(unitLocation, getEventLocation(event), {
                unit: 'mile',
            })
        );
    }
}

export function injectDistanceFromEvent(
    rows: EventQueueRowData[],
    unitLocation?: GeoPoint | null
): EventQueueRowData[] {
    if (unitLocation) {
        const localRows = cloneDeep(rows);
        for (const localRow of localRows) {
            localRow.distanceFromEvent = getDistanceFromEvent(localRow, unitLocation);
        }
        return localRows;
    }
    return rows;
}

export function convertToMaritime({
    format,
    coordinate,
    isLatitude,
}: {
    format: 'DMS' | 'DDM';
    coordinate?: number;
    isLatitude: boolean;
}): string {
    if (!coordinate || !format) return '';

    const direction = isLatitude ? (coordinate >= 0 ? 'N' : 'S') : coordinate >= 0 ? 'E' : 'W';

    // Absolute value to handle negative coordinate
    const absoluteDegrees = Math.abs(coordinate);

    const degrees = Math.floor(absoluteDegrees);
    const minutes = (absoluteDegrees - degrees) * 60;

    if (format === 'DDM') {
        return `${degrees}°${roundLatLng(minutes, 5)}'${direction}`;
    }

    if (format === 'DMS') {
        const flooredMinutes = Math.floor(minutes);
        const seconds = (absoluteDegrees - degrees - flooredMinutes / 60) * 3600;

        return `${degrees}°${roundLatLng(flooredMinutes, 3)}'${roundLatLng(seconds, 3)}"${direction}`;
    }

    return '';
}

const DMS_REGEXP_WITH_UNITS =
    /^\s*-?\d{1,3}°\s*\d{1,2}(\.\d{1,6})?'\s*\d{1,2}(\.\d{1,6})?"\s*[NSns]\s*,?\s*-?\d{1,3}°\s*\d{1,2}(\.\d{1,6})?'\s*\d{1,2}(\.\d{1,6})?"\s*[EWew]\s*$/;
const DMS_REGEXP =
    /^\s*-?\d{1,3}\s+\d{1,2}(\.\d{1,6})?\s+\d{1,2}(\.\d{1,6})?\s*[NSns]\s*,?\s*-?\d{1,3}\s+\d{1,2}(\.\d{1,6})?\s+\d{1,2}(\.\d{1,6})?\s*[EWew]\s*$/;
const DDM_REGEXP_WITH_UNITS =
    /^\s*-?\d{1,3}°\s*\d+(\.\d{1,6})?'?\s*[NSns]\s*,?\s*\d{1,3}°\s*\d+(\.\d{1,6})?'?\s*[EWew]\s*$/;
const DDM_REGEXP =
    /^\s*-?\d{1,3}\s+\d+(\.\d{1,6})?\s*[NSns]\s*,?\s*\d{1,3}\s+\d+(\.\d{1,6})?\s*[EWew]\s*$/;
const DD_REGEXP = /^\s*-?\d{1,3}(\.\d{1,6})?\s*,?\s*-?\d{1,3}(\.\d{1,6})?\s*$/;

export function getCoordinatesFormat(
    input: string,
    containsUnitsOfMeasure: boolean
): 'DD' | 'DDM' | 'DMS' | undefined {
    const ddmRegExp = containsUnitsOfMeasure ? DDM_REGEXP_WITH_UNITS : DDM_REGEXP;
    const dmsRegExp = containsUnitsOfMeasure ? DMS_REGEXP_WITH_UNITS : DMS_REGEXP;

    if (DD_REGEXP.test(input)) {
        return 'DD';
    } else if (ddmRegExp.test(input)) {
        return 'DDM';
    } else if (dmsRegExp.test(input)) {
        return 'DMS';
    }
}

export function maritimeToDecimal({
    degrees,
    minutes,
    seconds,
    direction,
}: {
    degrees: number;
    minutes: number;
    direction: string;
    seconds?: number;
}): number | undefined {
    if (degrees === undefined || minutes === undefined || direction === undefined) return;
    const isDMS = seconds !== undefined;

    let decimal = degrees + minutes / 60;

    if (isDMS) {
        decimal += seconds / 3600;
    }

    if (direction === 'S' || direction === 'W') {
        decimal = -decimal;
    }
    return roundLatLng(decimal, 6) as number;
}

export function convertFromDMStoDD(
    latStr: string,
    lonStr: string,
    containsUnitsOfMeasure: boolean
): string {
    const regExpWithUnits =
        /(-?\d{1,3})°\s*(\d{1,2}(?:\.\d+)?)'\s*(\d{1,2}(?:\.\d+)?)?"?\s*([NSnsWEwe])/;
    const regExpWithoutUnits =
        /(-?\d{1,3})\s+(\d{1,2}(?:\.\d+)?)\s+(\d{1,2}(?:\.\d+)?)?\s*([NSnsWEwe])/;
    const regExp = containsUnitsOfMeasure ? regExpWithUnits : regExpWithoutUnits;

    const latMatch = latStr.match(regExp);

    if (!latMatch) {
        return '';
    }
    const latDegrees = parseInt(latMatch[1], 10);
    const latMinutes = parseFloat(latMatch[2]);
    const latSeconds = parseFloat(latMatch[3]);
    const latDirection = latMatch[4];

    const lonMatch = lonStr.match(regExp);

    let longitude;

    if (lonMatch) {
        const lonDegrees = parseInt(lonMatch[1], 10);
        const lonMinutes = parseFloat(lonMatch[2]);
        const lonSeconds = parseFloat(lonMatch[3]);
        const lonDirection = lonMatch[4];

        longitude = maritimeToDecimal({
            degrees: lonDegrees,
            minutes: lonMinutes,
            seconds: lonSeconds,
            direction: lonDirection,
        });
    }

    const latitude = maritimeToDecimal({
        degrees: latDegrees,
        minutes: latMinutes,
        seconds: latSeconds,
        direction: latDirection,
    });

    return `${latitude}${longitude !== undefined ? ',' + longitude : ''}`;
}

export function convertFromDDMtoDD(
    latStr: string,
    lonStr: string,
    containsUnitsOfMeasure: boolean
): string {
    const regExpWithUnits = /(-?\d{1,3})°\s*(\d{1,2}(?:\.\d+)?)'?\s*([NSnsWEwe])/;
    const regExpWithoutUnits = /(-?\d{1,3})\s+(\d{1,2}(?:\.\d+)?)\s*([NSnsWEwe])/;
    const regExp = containsUnitsOfMeasure ? regExpWithUnits : regExpWithoutUnits;

    const latMatch = latStr.match(regExp);

    if (!latMatch) {
        return '';
    }
    const latDegrees = parseInt(latMatch[1], 10);
    const latMinutes = parseFloat(latMatch[2]);
    const latDirection = latMatch[3];

    const lonMatch = lonStr.match(regExp);
    let longitude;

    if (lonMatch) {
        const lonDegrees = parseInt(lonMatch[1], 10);
        const lonMinutes = parseFloat(lonMatch[2]);
        const lonDirection = lonMatch[3];

        longitude = maritimeToDecimal({
            degrees: lonDegrees,
            minutes: lonMinutes,
            direction: lonDirection,
        });
    }

    const latitude = maritimeToDecimal({
        degrees: latDegrees,
        minutes: latMinutes,
        direction: latDirection,
    });

    return `${latitude}${longitude !== undefined ? ',' + longitude : ''}`;
}

function convertMaritimeCoordinatesToDD(
    coordinates: string,
    inputFormat: 'DDM' | 'DMS',
    containsUnitsOfMeasure: boolean
): string {
    const regExp = containsUnitsOfMeasure
        ? /(-?\d+\s*°\s*\d+(\.\d+)?\s*'\s*\d+(\.\d+)?\s*"\s*[NSnsWEwe])|(-?\d+\s*°\s*\d+(\.\d+)?\s*'\s*[NSnsWEwe])/g
        : /(-?\d+\s+\d+(\.\d+)?\s+\d+(\.\d+)?\s*[NSnsWEwe])|(-?\d+\s+\d+(\.\d+)?\s*[NSnsWEwe])/g;

    const [latStr, lonStr] = coordinates.match(regExp) as string[];

    if (inputFormat === 'DDM') {
        return convertFromDDMtoDD(latStr, lonStr, containsUnitsOfMeasure);
    } else if (inputFormat === 'DMS') {
        return convertFromDMStoDD(latStr, lonStr, containsUnitsOfMeasure);
    }
    return coordinates;
}

export function reformatMaritimeCoordinates(coordinates: string): string {
    const containsUnitsOfMeasure = /(°+)|('+)|("+)/g.test(coordinates);
    const format = getCoordinatesFormat(coordinates, containsUnitsOfMeasure);

    switch (format) {
        case 'DD': {
            return coordinates;
        }
        case 'DDM': {
            return convertMaritimeCoordinatesToDD(coordinates, 'DDM', containsUnitsOfMeasure);
        }
        case 'DMS': {
            return convertMaritimeCoordinatesToDD(coordinates, 'DMS', containsUnitsOfMeasure);
        }
        default: {
            return coordinates;
        }
    }
}
